import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Input,
  List,
  Menu,
  NumberFormatter,
  rem,
  Select,
  Text,
  Textarea,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAdjustments,
  IconCaretDown,
  IconCircleCheck,
  IconCircleDashed,
  IconEdit,
  IconFileExport,
  IconInfoCircle,
  IconRefresh,
  IconSearch,
  IconStatusChange,
  IconTable,
  IconTimeDuration15,
  IconTrash,
  IconUpload,
  IconWash,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_SortingState,
  useMantineReactTable,
} from "mantine-react-table";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AllCommunityModule,
  CellSelectionChangedEvent,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  INumberCellEditorParams,
  RowSelectionOptions,
  StatusPanelDef,
  ValueFormatterParams,
} from "ag-grid-community";
import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import type { CustomCellRendererProps } from "ag-grid-react";
import { AgGridReact } from "ag-grid-react";
import {
  AdvancedFilterModule,
  ClientSideRowModelModule,
  ClipboardModule,
  ColDef,
  ColGroupDef,
  ColumnsToolPanelModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  GetRowIdParams,
  GridChartsModule,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsRequest,
  MasterDetailModule,
  MenuItemDef,
  MenuModule,
  ModuleRegistry,
  MultiFilterModule,
  RangeSelectionModule,
  RichSelectModule,
  RowClassParams,
  RowGroupingModule,
  RowStyle,
  ServerSideRowModelModule,
  SetFilterModule,
  SparklinesModule,
  StatusBarModule,
} from "ag-grid-enterprise";
import { modals } from "@mantine/modals";
import moment from "moment";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import _breadcrumb from "../../_base/component/_layout/_breadcrumb";
import {
  baseTheme,
  paginationPageSize,
  paginationPageSizeSelector,
} from "../../_base/_const/_constVar";
import useDynamicHeight from "../../_base/component/Core/hookBase";

ModuleRegistry.registerModules([ClientSideRowModelModule, AllCommunityModule]);

export interface TblOrderDetailCommand {
  /**
   * Nhận hoặc thiết lập mã định danh duy nhất cho dòng sản phẩm.
   */
  id: number;

  /**
   * Nhận hoặc thiết lập loại dòng.
   */
  lineTyle: string;

  /**
   * Nhận hoặc thiết lập mã định danh của tiêu đề đơn hàng.
   */
  orderHeaderId: number;

  /**
   * Nhận hoặc thiết lập mã định danh tổ chức.
   */
  orgId?: number;

  /**
   * Nhận hoặc thiết lập mã định danh bộ phận.
   */
  deptId?: number;

  /**
   * Nhận hoặc thiết lập ghi chú.
   */
  note: string;

  /**
   * Nhận hoặc thiết lập mã định danh sản phẩm.
   */
  itemId?: number;

  /**
   * Nhận hoặc thiết lập mã định danh liên kết với sản phẩm.
   */
  linkToItemId?: number;

  /**
   * Nhận hoặc thiết lập chất lượng.
   */
  quality?: number;

  /**
   * Nhận hoặc thiết lập mã đơn vị đo lường.
   */
  uomCode: string;

  /**
   * Nhận hoặc thiết lập mã đơn vị đo lường chính.
   */
  primaryUomCode: string;

  /**
   * Nhận hoặc thiết lập mã định danh bảng giá.
   */
  priceListId?: number;

  /**
   * Nhận hoặc thiết lập giá trên bảng giá.
   */
  priceOnPriceList?: number;

  /**
   * Nhận hoặc thiết lập tỷ lệ chiết khấu.
   */
  discountRate?: number;

  /**
   * Nhận hoặc thiết lập số tiền chiết khấu.
   */
  discountAmount?: number;

  /**
   * Nhận hoặc thiết lập chiết khấu thêm.
   */
  discountAdded?: number;

  /**
   * Nhận hoặc thiết lập giá cuối cùng.
   */
  priceFinal?: number;

  /**
   * Nhận hoặc thiết lập tỷ lệ VAT.
   */
  vatRate?: number;

  /**
   * Nhận hoặc thiết lập số tiền VAT.
   */
  vatAmount?: number;

  /**
   * Nhận hoặc thiết lập tổng số tiền của dòng sản phẩm.
   */
  totalLineAmount?: number;

  /**
   * Nhận hoặc thiết lập mã định danh khuyến mãi.
   */
  idPromote?: number;

  /**
   * Nhận hoặc thiết lập danh sách mã định danh khuyến mãi.
   */
  idPromoteList: string;

  /**
   * Nhận hoặc thiết lập mã định danh liên kết với dòng.
   */
  linkToLineId?: number;

  /**
   * Nhận hoặc thiết lập trạng thái kiểm tra in hóa đơn.
   */
  printInvoiceCheck?: number;

  /**
   * Nhận hoặc thiết lập thông tin khuyến mãi trả sau.
   */
  promotePostPaid: string;

  /**
   * Nhận hoặc thiết lập trạng thái kiểm tra khuyến mãi.
   */
  promoteClearCheck?: number;

  /**
   * Nhận hoặc thiết lập ngày xóa khuyến mãi.
   */
  promoteClearDate?: Date;

  /**
   * Nhận hoặc thiết lập mã định danh nhân viên xóa khuyến mãi.
   */
  prometeClearPersionId?: number;

  /**
   * Nhận hoặc thiết lập mã định danh khách hàng xóa khuyến mãi.
   */
  prometeClearClientId: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 1.
   */
  attribute1: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 2.
   */
  attribute2: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 3.
   */
  attribute3: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 4.
   */
  attribute4: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 5.
   */
  attribute5: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 6.
   */
  attribute6: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 7.
   */
  attribute7: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 8.
   */
  attribute8: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 9.
   */
  attribute9: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 10.
   */
  attribute10: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 11.
   */
  attribute11: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 12.
   */
  attribute12: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 13.
   */
  attribute13: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 14.
   */
  attribute14: string;

  /**
   * Nhận hoặc thiết lập thuộc tính 15.
   */
  attribute15: string;

  /**
   * Nhận hoặc thiết lập mã định danh khách hàng.
   */
  clientId?: number;

  /**
   * Nhận hoặc thiết lập mã định danh ca làm việc.
   */
  workShiftId?: number;

  /**
   * Nhận hoặc thiết lập mã định danh người tạo.
   */
  createBy?: number;

  /**
   * Nhận hoặc thiết lập ngày tạo.
   */
  createDate?: Date;

  /**
   * Nhận hoặc thiết lập mã định danh người cập nhật cuối cùng.
   */
  lastUpdateBy?: number;

  /**
   * Nhận hoặc thiết lập ngày cập nhật cuối cùng.
   */
  lastUpdateDate?: Date;

  /**
   * Nhận hoặc thiết lập mã sản phẩm.
   */
  itemCode: string;

  /**
   * Nhận hoặc thiết lập mã định danh kho phụ.
   */
  subInvId?: number;

  attribute16: string;
  attribute17: string;
  attribute18: string;
  attribute19: string;
  attribute20: string;
  datewarranty?: Date;
  attribute21: string;
  attribute22: string;
  attribute23: string;
  attribute24: string;
  attribute25: string;
  attribute26: string;
  attribute27: string;
  attribute28: string;
  attribute29: string;
  attribute30: string;
  attribute31: string;
  attribute32: string;
  attribute33: string;
  attribute34: string;
  attribute35: string;
  attribute36: string;
  attribute37: string;
  attribute38: string;
  attribute39: string;
  attribute40: string;
  attribute41: string;
  attribute42: string;
  attribute44: string;
  attribute43: string;
  attribute45: string;
  attribute46: string;
  attribute47: string;
  amountTotalOrder?: number;
  amountDiscountOrder?: number;
  amountPaidOrder?: number;
  amountRemainingOrder?: number;
  amountVatOrder?: number;
  amountVoucherOrder?: number;
  orderDate: Date;
  custId: number;
  itemName: string;
  isNoSerial?: boolean;
}

const DetailsSellView = ({ e, amount }: { e: any; amount: number }) => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const gridRef = useRef<AgGridReact<TblOrderDetailCommand>>(null);

  const height = useDynamicHeight(280);
  const containerStyle = useMemo(
    () => ({ width: "100%", height: height, marginTop: "15px" }),
    [height],
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const [rowData, setRowData] = useState<TblOrderDetailCommand[]>([]);
  useEffect(() => {
    setRowData(e);
    console.log(e);
  }, []);
  function currencyFormatter(params: any) {
    return formatNumber(params);
  }
  function formatNumber(number: any) {
    return Math.floor(number).toLocaleString();
  }
  const columnDefs = useMemo<ColDef<TblOrderDetailCommand>[]>(
    () => [
      {
        field: "itemCode",
        headerName: "Mã sản phẩm",
        editable: false,
        pinned: "left",
        width: 150,
        filter: "agTextColumnFilter",
      },
      {
        field: "itemName",
        headerName: "Tên sản phẩm",
        editable: false,
        width: 400,
        filter: "agTextColumnFilter",
      },
      {
        field: "lineTyle",
        headerName: "Loại mặt hàng",
        editable: false,
        lockPinned: true,
        filter: "agTextColumnFilter",
        width: 150,
        cellRenderer: (
          params: CustomCellRendererProps<TblOrderDetailCommand>,
        ) => {
          let valueee = "";
          if (params.data?.lineTyle === "HH") valueee = "Hàng hóa";
          else if (params.data?.lineTyle === "KM") valueee = "Khuyến mại";
          else if (params.data?.lineTyle === "DV") valueee = "Dịch vụ";
          else if (params.data?.lineTyle === "HK") valueee = "Hàng kèm";
          else valueee = params.data?.lineTyle ?? "Khác";
          return <>{valueee}</>;
        },
      },
      {
        field: "attribute23",
        headerName: "Mã kho",
        editable: false,
        width: 100,
        filter: "agTextColumnFilter",
      },
      {
        field: "primaryUomCode",
        headerName: "Đơn vị tính",
        editable: false,
        lockPinned: true,
        filter: "agTextColumnFilter",
        width: 150,
      },
      {
        field: "quality",
        headerName: "Số lượng",
        editable: false,
        width: 120,
        filter: "agNumberColumnFilter",
      },
      {
        field: "discountRate",
        headerName: "Chiết khấu",
        editable: false,
        lockPinned: true,
        filter: "agNumberColumnFilter",
        width: 150,
        cellRenderer: (
          params: CustomCellRendererProps<TblOrderDetailCommand>,
        ) => {
          return (
            <>
              {(params.data?.discountRate ?? 0 > 0)
                ? params.data?.discountRate + "%"
                : currencyFormatter(params.data?.discountAmount ?? 0)}
            </>
          );
        },
      },
      {
        field: "discountAdded",
        headerName: "Giảm thêm đơn",
        editable: false,
        width: 150,
        filter: "agNumberColumnFilter",
        cellRenderer: (
          params: CustomCellRendererProps<TblOrderDetailCommand>,
        ) => {
          return <>{currencyFormatter(params.data?.discountAdded ?? 0)}</>;
        },
      },
      {
        field: "priceFinal",
        headerName: "Tiền sau CK",
        editable: false,
        lockPinned: true,
        filter: "agNumberColumnFilter",
        width: 150,
        cellRenderer: (
          params: CustomCellRendererProps<TblOrderDetailCommand>,
        ) => {
          return <>{currencyFormatter(params.data?.priceFinal ?? 0)}</>;
        },
      },
      {
        field: "vatRate",
        headerName: "VAT (%)",
        editable: false,
        width: 100,
        filter: "agNumberColumnFilter",
      },
      {
        field: "vatAmount",
        headerName: "Tiền sau VAT",
        editable: false,
        lockPinned: true,
        filter: "agNumberColumnFilter",
        width: 150,
        cellRenderer: (
          params: CustomCellRendererProps<TblOrderDetailCommand>,
        ) => {
          return <>{currencyFormatter(params.data?.vatAmount ?? 0)}</>;
        },
      },
      {
        field: "attribute28",
        headerName: "KM phân bổ",
        editable: false,
        width: 150,
        filter: "agNumberColumnFilter",
        cellRenderer: (
          params: CustomCellRendererProps<TblOrderDetailCommand>,
        ) => {
          return <>{currencyFormatter(params.data?.attribute28 ?? 0)}</>;
        },
      },
      {
        field: "attribute29",
        headerName: "Xin giảm phân bổ",
        editable: false,
        lockPinned: true,
        filter: "agNumberColumnFilter",
        width: 200,
        cellRenderer: (
          params: CustomCellRendererProps<TblOrderDetailCommand>,
        ) => {
          return <>{currencyFormatter(params.data?.attribute29 ?? 0)}</>;
        },
      },
      {
        field: "totalLineAmount",
        headerName: "Thành tiền",
        editable: false,
        width: 150,
        pinned: "right",
        filter: "agNumberColumnFilter",
        cellRenderer: (
          params: CustomCellRendererProps<TblOrderDetailCommand>,
        ) => {
          return <>{currencyFormatter(params.data?.totalLineAmount ?? 0)}</>;
        },
      },
      {
        field: "attribute35",
        headerName: "Thành tiền sau phân bổ",
        editable: false,
        width: 200,
        filter: "agNumberColumnFilter",
        cellRenderer: (
          params: CustomCellRendererProps<TblOrderDetailCommand>,
        ) => {
          return <>{currencyFormatter(params.data?.attribute35 ?? 0)}</>;
        },
      },
      {
        field: "attribute22",
        headerName: "Serial",
        editable: false,
        width: 200,
        filter: "agTextColumnFilter",
      },
      {
        field: "attribute20",
        headerName: "Bảo hành(tháng)",
        editable: false,
        width: 180,
        filter: "agTextColumnFilter",
      },
      {
        field: "note",
        headerName: "Ghi chú",
        editable: false,
        width: 200,
        filter: "agTextColumnFilter",
      },
    ],
    [rowData],
  );

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      editable: false,
      filter: true,
      floatingFilter: true,
    };
  }, []);

  const getContextMenuItems = useCallback(
    (
      params: GetContextMenuItemsParams<any>,
    ): (DefaultMenuItem | MenuItemDef)[] => {
      const _number = params?.node?.data?.numberName;
      var result: (DefaultMenuItem | MenuItemDef)[] = [
        {
          name: "Đẩy lên Asia chứng từ: " + _number,
          action: async () => {
            if (!params?.node?.data?.numberName) {
              NotificationExtension.Info("Mã chứng từ rỗng !");
              return;
            } else {
            }
          },
          cssClasses: ["red", "bold"],
        },
        {
          name: "Cập nhật lại trên Asia chứng từ: " + _number,
          action: async () => {
            if (!params?.node?.data?.numberName) {
              NotificationExtension.Info("Mã chứng từ rỗng !");
              return;
            }
          },
          cssClasses: ["red", "bold"],
        },
        {
          // custom item
          name: "Xóa trên Asia chứng từ: " + _number,
          action: async () => {
            if (!params?.node?.data?.numberName) {
              NotificationExtension.Info("Mã chứng từ rỗng !");
              return;
            }
          },
          cssClasses: ["red", "bold"],
        },

        // "cut",
        "copy",
        "export",
      ];
      return result;
    },
    [window, rowData],
  );
  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    return { mode: "multiRow" };
  }, []);

  const statusBar = useMemo<{
    statusPanels: StatusPanelDef[];
  }>(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalAndFilteredRowCountComponent" },
        { statusPanel: "agTotalRowCountComponent" },
        { statusPanel: "agFilteredRowCountComponent" },
        { statusPanel: "agSelectedRowCountComponent" },
        { statusPanel: "agAggregationComponent" },
      ],
    };
  }, []);

  return (
    <>
      <Text fw={600} mt={10}>
        Tổng tiền:{" "}
        <NumberFormatter
          value={amount || 0}
          suffix="₫"
          thousandSeparator="."
          decimalSeparator=","
        />
      </Text>
      <div style={containerStyle}>
        <div style={gridStyle} className={"ag-theme-quartz"}>
          <AgGridReact<TblOrderDetailCommand>
            ref={gridRef}
            rowData={rowData}
            theme={baseTheme}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            suppressColumnVirtualisation={true}
            suppressRowVirtualisation={true}
            pagination={true}
            localeText={AG_GRID_LOCALE_VN}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            cacheBlockSize={paginationPageSize}
            maxBlocksInCache={3}
            getContextMenuItems={getContextMenuItems}
            statusBar={statusBar}
          />
        </div>
      </div>
    </>
  );
};

export default DetailsSellView;
